import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useRequest } from 'ahooks';
import TipContext from '@home/components/TipContext';
import CommonContainer from '../components/CommonContainer';
import GrossRevenueCard from './components/LiveTitleCard';
import { reportOperationLog } from '@gp/services/global';
import { fetchProjectFilterData } from '@gp/pages/StudioMonthlyReport/PerformanceOverview/services';
import { fetchLiveTitleData, liveTitleDownLoad } from './services';
import { getArraySort, getFirstDayOfMonth, getLastDayOfMonth } from '@gp/utils/common';
import { getTableRecords, getTableOptions } from './utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import { SORT_REQ_MAP } from './constants';

import type { SortBy } from 'tea-component/lib/table/addons';
import type { TableListOptionsType } from '@gp/components/GeneralTable/index.d';
import type { LiveTitleDataType } from './types/services';

import './index.scss';

interface IStudioOption {
  value: string;
  text: string;
  projects: {
    id: string;
    name: string;
  }[];
  [keyName: string]: any;
}

const LiveTitle: React.FC = () => {
  // 头部显示的时间
  const [headerDate, setHeaderDate] = useState('');
  // 控制头部时间只取一次
  const isFinishHeaderDate = useRef(false);
  // 表头显示的时间
  const [tableHeaderDate, setTableHeaderDate] = useState('');
  const [filterTimeRange, setFilterTimeRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);
  // 通知Project筛选器刷新下拉项及重新全选
  const [projectFilterRefreshRenderKey, setProjectFilterRefreshRenderKey] = useState(0);
  // studio全选时project筛选器的下拉项
  const allProjectFilterOptionsRef = useRef<IGPSelectOptionWithGroup[]>([]);
  const [projectFilterOptions, setProjectFilterOptions] = useState<IGPSelectOptionWithGroup[]>([]);
  const paramsListRef = useRef<{
    select_id: string[];
    start_date: string;
    end_date: string;
    yoy: boolean;
    mom: boolean;
    order: 'desc' | 'asc' | undefined;
    order_metric: string;
  }>({
    select_id: [],
    start_date: '',
    end_date: '',
    yoy: true,
    mom: false,
    order: 'desc',
    order_metric: 'Gross Revenue',
  });
  const studioFilterOptionsRef = useRef<IStudioOption[]>([]);

  const { loading: filterLoading, data: filterData } = useRequest(fetchProjectFilterData, {
    defaultParams: [
      {
        page: 'live_title',
      },
    ],
  });

  const {
    loading: liveTitleLoading,
    data: liveTitleData,
    run: liveTitleRun,
  } = useRequest(fetchLiveTitleData, { manual: true });

  const studioFilterOptions = useMemo(() => {
    if (filterData?.start_date && filterData?.end_date) {
      setFilterTimeRange({
        startDate: filterData?.start_date,
        endDate: filterData?.end_date,
      });
      setTableHeaderDate(filterData?.end_date);
    }
    const studioList = filterData?.list;
    let newProjectOptions: {
      id: string;
      name: string;
    }[] = [];
    if (!studioList || !Array.isArray(studioList)) {
      studioFilterOptionsRef.current = [];
      return [];
    }
    const _options =
      getArraySort(studioList, 'string', 'asc', 'name')?.map((t) => {
        const currentItemProjects = t?.projects || [];
        newProjectOptions = [...newProjectOptions, ...currentItemProjects];
        return {
          value: t?.id || '',
          text: t?.name || '-',
          label: t?.name || '-',
          projects: t?.projects,
          groupKey: 'separated',
        };
      }) || [];

    setProjectFilterRefreshRenderKey(Date.now());
    const allProjectOptions =
      getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map((t: { name: string; id: string }) => ({
        value: t?.id || '',
        text: t?.name || '-',
        label: t?.name || '-',
        groupKey: 'separated',
      })) || [];
    setProjectFilterOptions(allProjectOptions);
    allProjectFilterOptionsRef.current = allProjectOptions;

    studioFilterOptionsRef.current = _options;
    return _options;
  }, [filterLoading]);

  const filterOptions = useMemo(
    () => [
      {
        refreshRenderKey: projectFilterRefreshRenderKey,
        type: 'multipleselect',
        fieldName: 'project',
        value: [],
        option: projectFilterOptions,
        className: 'dbg-live-title-card-filter-project-selector',
        style: { marginRight: 10 },
        label: 'Project',
        disabled: filterLoading,
        placeholder: 'Please select Project',
      },
      {
        type: 'multipleselect',
        fieldName: 'studio',
        value: [],
        option: studioFilterOptions,
        className: 'dbg-live-title-card-filter-studio-selector',
        style: { marginRight: 10 },
        label: 'Studio',
        disabled: filterLoading,
        placeholder: 'Please select Studio',
      },
    ],
    [filterLoading, studioFilterOptions, projectFilterOptions, projectFilterRefreshRenderKey],
  );

  const tableOptions: TableListOptionsType = useMemo(
    () =>
      getTableOptions(
        liveTitleLoading,
        liveTitleData as LiveTitleDataType,
        tableHeaderDate,
        paramsListRef?.current?.yoy,
        paramsListRef?.current?.mom,
      ),
    [liveTitleLoading, tableHeaderDate],
  );

  const tableRecords = useMemo(
    () => getTableRecords(liveTitleLoading, liveTitleData as LiveTitleDataType),
    [liveTitleLoading],
  );

  const [downloadDateValue, setDownloadDateValue] = useState<[string, string]>(['', '']);

  const handleDownloadDateChange = useCallback((payload?: [string, string]) => {
    if (!payload || !Array.isArray(payload)) return;
    setDownloadDateValue(payload);
  }, []);

  const handleDownloadOpen = useCallback(() => {
    const { start_date: startDate, end_date: endDate } = paramsListRef.current || {};
    const startDateTmp = startDate || '';
    const endDateTmp = endDate || '';
    setDownloadDateValue([startDateTmp, endDateTmp]);
  }, []);

  const handleTableSort = useCallback((v: SortBy<any>) => {
    paramsListRef.current = {
      ...paramsListRef.current,
      order: v?.order,
      order_metric: SORT_REQ_MAP?.[v?.by],
    };
    liveTitleRun(paramsListRef.current);
  }, []);

  const handleFilterChange = useCallback(
    (studio: string[], project: string[], date: string, ym: string[], handleFilterName: string) => {
      let selectId: string[] = [];
      let newProjectOptions: {
        id: string;
        name: string;
      }[] = [];
      if (handleFilterName === 'project') {
        selectId = project;
        // studio筛选器变化时需要改变project筛选器的筛选项，并且project筛选器要重新全选
      } else if (handleFilterName === 'studio') {
        // 全选
        if (!studio?.length) {
          setProjectFilterRefreshRenderKey(Date.now());
          setProjectFilterOptions(allProjectFilterOptionsRef.current);
        } else {
          for (let i = 0, l = studio?.length; i < l; i++) {
            const t = studio?.[i];
            const studioFilterOptions = studioFilterOptionsRef.current;
            if (Array.isArray(studioFilterOptions)) {
              for (let j = 0, l = studioFilterOptions?.length; j < l; j++) {
                const k = studioFilterOptions?.[j];
                if (k?.value === t && Array.isArray(k?.projects)) {
                  const currentItemProjects = k?.projects || [];
                  const currentItemProjectIds = currentItemProjects?.map((item) => item?.id) || [];
                  newProjectOptions = [...newProjectOptions, ...currentItemProjects];
                  selectId = [...selectId, ...currentItemProjectIds];
                }
              }
            }
            if (i === l - 1) {
              setProjectFilterRefreshRenderKey(Date.now());
              setProjectFilterOptions(
                getArraySort(newProjectOptions, 'string', 'asc', 'name')?.map((t: { name: string; id: string }) => ({
                  value: t?.id || '',
                  text: t?.name || '-',
                  label: t?.name || '-',
                  groupKey: 'separated',
                })) || [],
              );
            }
          }
        }
      } else {
        selectId = paramsListRef?.current?.select_id;
      }
      const paramsDate = {
        startDate: '',
        endDate: '',
      };
      if (date) {
        const startDate = getFirstDayOfMonth(date);
        paramsDate.startDate = startDate;
        paramsDate.endDate = getLastDayOfMonth(date);
        setTableHeaderDate(startDate);
      }
      paramsListRef.current = {
        ...paramsListRef.current,
        select_id: selectId,
        start_date: paramsDate?.startDate,
        end_date: paramsDate?.endDate,
        yoy: ym?.includes('1'),
        mom: ym?.includes('2'),
      };
      liveTitleRun(paramsListRef.current);
    },
    [],
  );

  // 页面打点
  useEffect(() => {
    reportOperationLog(ALL_REPORT_OPERATION_LOG.STUDIO_MONTHLY_REPORT_LIVE_TITLE as any);
  }, []);

  // 组织头部显示的时间
  useEffect(() => {
    if (isFinishHeaderDate.current || liveTitleLoading) return;
    const date = liveTitleData?.date_time || '';
    if (date) {
      setHeaderDate(date);
      isFinishHeaderDate.current = true;
    }
  }, [liveTitleLoading]);

  useEffect(() => {
    if (filterLoading) return;
    const { endDate } = filterTimeRange || {};
    const startDateTmp = getFirstDayOfMonth(endDate);
    const endDateTmp = getLastDayOfMonth(endDate);
    paramsListRef.current = {
      ...paramsListRef.current,
      start_date: startDateTmp,
      end_date: endDateTmp,
    };
    setDownloadDateValue([startDateTmp, endDateTmp]);
    // 初始化请求数据
    liveTitleRun(paramsListRef.current);
  }, [filterLoading, filterTimeRange]);

  return (
    <CommonContainer title="Live Title" subTitle={`Latest Data: ${headerDate || '-'}`} loading={false}>
      <div className="dbg-studio-monthly-report-live-title">
        <GrossRevenueCard
          title={
            <div className="dbg-studio-monthly-report-card-title">
              Live Title
              <TipContext
                className="globalpedia-modal-font14 dbg-studio-monthly-report-card-title-info"
                placement="right"
                AWidth={555}
                tipText={
                  <>
                    <p>【Metrics】: Actual (Revenue & Profit)</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Reporting Package</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Monthly</p>
                    <br />
                    <p>【Metrics】: HC & HC Cost</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Rolling Forecast</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                    <br />
                    <p>【Metrics】: Staff Month Rate</p>
                    <p>{CONSTANT_TEXTS.DATA_SOURCE}: Calculate</p>
                    <p>{CONSTANT_TEXTS.UPDATE_FREQUENCY}: Quarterly</p>
                  </>
                }
              />
            </div>
          }
          filterLoading={filterLoading}
          filterTimeRangeData={filterTimeRange}
          filterOptions={filterOptions}
          filterChange={handleFilterChange}
          tableClassName="dbg-studio-monthly-report-live-title-list dbg-table-sticky"
          tableLoading={liveTitleLoading}
          tableOptions={tableOptions}
          tableRecords={tableRecords}
          onTableSort={handleTableSort}
          uniqueDownloadId="studio_monthly_report_live_title_download"
          downloadParams={{
            ...paramsListRef.current,
            // @ts-ignore
            start_date: getFirstDayOfMonth(downloadDateValue?.[0]),
            // @ts-ignore
            end_date: getLastDayOfMonth(downloadDateValue?.[1]),
          }}
          downloadServices={liveTitleDownLoad}
          downloadLogKey={{
            csv: 'STUDIO_MONTHLY_REPORT_LIVE_TITLE_CSV_DOWNLOAD_SUBMIT',
            excel: 'STUDIO_MONTHLY_REPORT_LIVE_TITLE_EXCEL_DOWNLOAD_SUBMIT',
          }}
          downloadDateValue={downloadDateValue}
          onDownloadDateChange={handleDownloadDateChange}
          onDownloadOpen={handleDownloadOpen}
        />
      </div>
    </CommonContainer>
  );
};

export default React.memo(LiveTitle);
